import { useEffect, useMemo, useState } from "react";
import { navLinks } from "@/mocks/siteData";
import { useActiveSection } from "@/hooks/useActiveSection";

type SiteHeaderProps = {
  onOpenConsult: () => void;
};

export default function SiteHeader({ onOpenConsult }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const sectionIds = useMemo(() => navLinks.map((link) => link.id), []);
  const activeId = useActiveSection(sectionIds);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled || menuOpen
          ? "bg-background-50/95 backdrop-blur-md border-b border-primary-900/40"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-10 h-16 md:h-[72px] flex items-center justify-between">
        <a href="#hero" className="flex items-center gap-2.5 cursor-pointer">
          <span className="font-heading text-2xl md:text-3xl font-semibold tracking-tight text-primary-400">
            Y
          </span>
          <span className="text-sm md:text-base font-medium text-foreground-900">
            연안<span className="text-primary-400">법무법인</span>
          </span>
          <span className="hidden lg:inline font-label text-[10px] text-foreground-500 ml-1">
            Yeonan Law Firm
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-7">
          {navLinks.map((link) => {
            const isActive = link.id === activeId;
            return (
              <a
                key={link.id}
                href={`#${link.id}`}
                aria-current={isActive ? "true" : undefined}
                className={`relative text-sm transition-colors duration-300 cursor-pointer whitespace-nowrap ${
                  isActive
                    ? "text-primary-400 font-medium"
                    : "text-foreground-700 hover:text-primary-400"
                }`}
              >
                {link.label}
                <span
                  className={`absolute -bottom-1.5 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-primary-400 transition-opacity duration-300 ${
                    isActive ? "opacity-100" : "opacity-0"
                  }`}
                />
              </a>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onOpenConsult}
            className="hidden md:inline-flex items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors duration-300 cursor-pointer"
          >
            상담예약
          </button>
          <button
            type="button"
            aria-label="메뉴 열기"
            onClick={() => setMenuOpen((v) => !v)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-900 cursor-pointer"
          >
            <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} />
          </button>
        </div>
      </div>

      {menuOpen ? (
        <div className="md:hidden bg-background-50/98 backdrop-blur-md border-t border-background-200/60">
          <div className="px-4 py-4 flex flex-col">
            {navLinks.map((link) => {
              const isActive = link.id === activeId;
              return (
                <a
                  key={link.id}
                  href={`#${link.id}`}
                  onClick={() => setMenuOpen(false)}
                  aria-current={isActive ? "true" : undefined}
                  className={`py-3 text-sm transition-colors duration-300 cursor-pointer border-b border-background-200/40 flex items-center justify-between ${
                    isActive
                      ? "text-primary-400 font-medium"
                      : "text-foreground-700 hover:text-primary-400"
                  }`}
                >
                  {link.label}
                  <span
                    className={`h-1.5 w-1.5 rounded-full bg-primary-400 transition-opacity duration-300 ${
                      isActive ? "opacity-100" : "opacity-0"
                    }`}
                  />
                </a>
              );
            })}
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                onOpenConsult();
              }}
              className="mt-4 inline-flex items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors duration-300 cursor-pointer"
            >
              상담예약
            </button>
          </div>
        </div>
      ) : null}
    </header>
  );
}